(() => {
  if (customElements.get('two-nu-fit3d')) return;
  let viewerPromise;
  function loadViewer(url) {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!viewerPromise) viewerPromise = import(url).then(() => customElements.whenDefined('model-viewer')).catch(error => { viewerPromise = null; throw error; });
    return viewerPromise;
  }
  class Fit3D extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) this.init();
      this.modal = this.closest('modal-dialog');
      if (!this.modal) return;
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.syncOpen());
      this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      this.resizeObserver?.observe(this.stage);
      this.syncOpen();
    }
    disconnectedCallback() { this.observer?.disconnect(); this.resizeObserver?.disconnect(); cancelAnimationFrame(this.raf); }
    init() {
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-fit-config]').textContent);
      this.words = JSON.parse(this.querySelector('[data-fit-words]').textContent);
      this.stage = this.querySelector('[data-fit-stage]');
      this.poster = this.querySelector('[data-fit-poster]');
      this.status = this.querySelector('[data-fit-status]');
      this.svg = this.querySelector('[data-fit-lines]');
      this.mode = 'frame';
      this.currentView = 'front';
      this.addEventListener('click', event => {
        const action = event.target.closest('[data-fit-action]');
        if (!action) return;
        const name = action.dataset.fitAction;
        if (name === 'retry') { this.retries = (this.retries || 0) + 1; this.viewer?.remove(); this.viewer = null; this.ready = false; this.loading = false; this.start(); }
        else if (name === 'frame' || name === 'lens') this.select(name);
        else if (this.viewer) this.view(name);
      });
      this.resizeObserver = new ResizeObserver(() => { if(this.ready && this.currentView !== 'free') this.fitView(this.currentView); this.scheduleLines(); });
      this.resizeObserver.observe(this.stage);
      this.writeMetrics();
    }
    syncOpen() {
      if (this.modal.hasAttribute('open')) {
        this.start();
        if (this.ready) { this.viewer?.dismissPoster(); this.select('frame'); }
      } else {
        this.viewer?.showPoster();
        this.svg.replaceChildren();
        cancelAnimationFrame(this.raf);
      }
    }
    async start() {
      if (this.viewer || this.loading) return;
      this.loading = true;
      this.dataset.state = 'loading';
      this.poster.src = this.config.poster;
      this.status.textContent = this.words.loading;
      this.querySelector('[data-fit-retry]').hidden = true;
      try {
        const moduleUrl = new URL(this.dataset.viewerUrl, location.href);
        if (this.retries) moduleUrl.searchParams.set('fit_retry', String(this.retries));
        await loadViewer(moduleUrl.href);
        
        if (!this.isConnected || !this.modal.hasAttribute('open')) { this.loading = false; return; }
        const v = document.createElement('model-viewer');
        customElements.get('model-viewer').dracoDecoderLocation = new URL('.', moduleUrl.href).href;
        this.viewer = v;
        v.setAttribute('alt', this.words.modelAlt.replace('{{ model }}', this.config.name));
        v.setAttribute('camera-controls', '');
        v.setAttribute('disable-pan', '');
        v.setAttribute('disable-tap', '');
        v.setAttribute('touch-action', 'none');
        v.setAttribute('interaction-prompt', 'none');
        v.setAttribute('camera-orbit', '0deg 90deg 100%');
        v.setAttribute('field-of-view', '30deg');
        v.setAttribute('min-field-of-view', '30deg');
        v.setAttribute('max-field-of-view', '45deg');
        v.setAttribute('min-camera-orbit', 'auto 0deg 0.1m');
        v.setAttribute('max-camera-orbit', 'auto 180deg 30m');
        v.setAttribute('environment-image', 'neutral');
        v.setAttribute('exposure', '1');
        v.setAttribute('shadow-intensity', '0');
        v.setAttribute('loading', 'eager');
        v.setAttribute('reveal', 'auto');
        v.addEventListener('load', () => {
          clearTimeout(this.loadTimer);
          this.ready = true; this.loading = false; this.dataset.state = 'ready';
          this.installOrthographicRenderer();
          this.status.textContent = this.words.drag;
          this.originalColors = new Map();
          for (const material of v.model.materials) this.originalColors.set(material.name, [...material.pbrMetallicRoughness.baseColorFactor]);
          this.select('frame');
          if (!this.modal.hasAttribute('open')) v.showPoster();
        });
        v.addEventListener('error', () => this.fail());
        v.addEventListener('camera-change', event => {
          if(event.detail?.source === 'user-interaction' && !this.settingView) { this.currentView='free';this.updateViewButtons(); }
          this.scheduleLines();
        });
        v.addEventListener('pointerdown', event => { this.pointer = { x: event.clientX, y: event.clientY, id: event.pointerId }; });
        v.addEventListener('pointerup', event => {
          if (!this.ready || !this.pointer || event.pointerId !== this.pointer.id) return;
          const moved = Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y);
          this.pointer = null;
          if (moved > 7) return;
          const material = v.materialFromPoint(event.clientX, event.clientY);
          if (material?.name.startsWith('lens')) this.select('lens', material.name);
          else if (material) this.select('frame');
        });
        v.addEventListener('pointercancel', () => { this.pointer = null; });
        this.stage.insertBefore(v, this.svg);
        const modelUrl = new URL(this.config.src, location.href);
        if (this.retries) modelUrl.searchParams.set('fit_retry', String(this.retries));
        v.src = modelUrl.href;
        this.loadTimer = setTimeout(() => { if (!this.ready) this.fail(); }, 30000);
      } catch (error) { this.fail(); }
    }
    fail() {
      clearTimeout(this.loadTimer); this.loading = false; this.dataset.state = 'error';
      this.status.textContent = this.words.error;
      this.querySelector('[data-fit-retry]').hidden = false;
    }
    select(mode, partName) {
      this.mode = mode;
      this.partName = partName || (this.config.parts['lens-left'] ? 'lens-left' : 'lens-shield');
      this.querySelectorAll('[data-fit-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fitMode === mode)));
      if (this.ready) {
        for (const material of this.viewer.model.materials) {
          const original = this.originalColors.get(material.name);
          material.pbrMetallicRoughness.setBaseColorFactor(mode === 'lens' && material.name === this.partName ? [.50, .66, .51, 1] : original);
        }
      }
      this.writeMetrics();
      if(this.ready) this.fitView('front');
      this.scheduleLines();
    }
    writeMetrics() {
      const lens = this.config.parts[this.partName] || Object.values(this.config.parts)[0];
      const d = this.config.dimensions;
      const labels = this.mode === 'frame' ? [this.words.width, this.words.length] : [this.words.lensWidth, this.words.lensHeight];
      const values = this.mode === 'frame' ? [d.width, d.length] : [lens.width, lens.height];
      this.querySelectorAll('[data-fit-metric-label]').forEach((node, i) => { node.textContent = labels[i]; });
      this.querySelectorAll('[data-fit-metric-value]').forEach((node, i) => { node.textContent = values[i].toFixed(1); });
      this.querySelector('[data-fit-measure-note]').textContent = this.mode === 'frame' ? this.words.frameNote : (this.config.code === 'VV' ? this.words.shieldNote : this.words.lensNote);
    }
    installOrthographicRenderer() {
      const element=this.viewer;
      // Public Model Viewer effect-composer hook: retain its scene, lighting,
      // controls and loader, but render a true parallel projection.
      const adapter={
        setRenderer(renderer){this.renderer=renderer;},
        setMainScene(scene){this.scene=scene;},
        setMainCamera(camera){this.camera=camera;},
        setSize(){},
        beforeRender(){this.updateProjection();},
        updateProjection(){
          if(!this.camera || !element.isConnected) return;
          const halfHeight=element.getCameraOrbit().radius*Math.tan(element.getFieldOfView()*Math.PI/360);
          const halfWidth=halfHeight*this.camera.aspect;
          this.camera.isPerspectiveCamera=false;this.camera.isOrthographicCamera=true;
          this.camera.near=0.0001;this.camera.far=element.getCameraOrbit().radius+1;
          this.camera.projectionMatrix.makeOrthographic(-halfWidth,halfWidth,halfHeight,-halfHeight,this.camera.near,this.camera.far);
          this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();
        },
        render(){
          this.updateProjection();this.renderer.autoClear=true;this.renderer.toneMapping=this.scene.toneMapping;
          this.renderer.render(this.scene,this.camera);
        }
      };
      element.registerEffectComposer(adapter);this.orthographicAdapter=adapter;
    }
    updateViewButtons() {
      this.dataset.view = this.currentView;
      this.querySelectorAll('[data-fit-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fitView === this.currentView)));
    }
    view(name) {
      if(name==='reset') { this.select('frame'); return; }
      if(!['front','side','top'].includes(name)) return;
      if(name!=='front' && this.mode==='lens') this.select('frame');
      this.fitView(name);
    }
    fitView(name) {
      if(!this.ready || !this.stage.clientWidth || !this.stage.clientHeight) return;
      this.currentView=name;this.updateViewButtons();this.settingView=true;
      const v=this.viewer,w=this.stage.clientWidth,h=this.stage.clientHeight;
      const lens=this.config.parts[this.partName]||Object.values(this.config.parts)[0];
      const cloud=this.mode==='lens'?lens.visibleOutline:this.config.frameOutlines[name];
      const angles={front:[0,90],side:[90,90],top:[0,0]};
      const [theta,phi]=angles[name],t=theta*Math.PI/180,p=phi*Math.PI/180;
      const right=[Math.cos(t),0,-Math.sin(t)],up=[-Math.cos(p)*Math.sin(t),Math.sin(p),-Math.cos(p)*Math.cos(t)];
      const dot=(a,b)=>a.reduce((s,x,i)=>s+x*b[i],0);
      const horizontal=cloud.map(q=>dot(q,right)),vertical=cloud.map(q=>dot(q,up));
      const xmin=Math.min(...horizontal),xmax=Math.max(...horizontal),ymin=Math.min(...vertical),ymax=Math.max(...vertical);
      const padding=this.mode==='lens'?{left:82,right:30,top:58,bottom:26}:name==='top'?{left:28,right:88,top:54,bottom:26}:{left:30,right:30,top:58,bottom:24};
      const scale=Math.min(Math.max(80,w-padding.left-padding.right)/(xmax-xmin),Math.max(90,h-padding.top-padding.bottom)/(ymax-ymin));
      const centre=[0,1,2].map(i=>(Math.min(...cloud.map(q=>q[i]))+Math.max(...cloud.map(q=>q[i])))/2);
      const dx=(padding.left-padding.right)/2,dy=(padding.top-padding.bottom)/2;
      const cx=(xmin+xmax)/2,cy=(ymin+ymax)/2;
      const target=centre.map((x,i)=>x+right[i]*(cx-dot(centre,right)-dx/scale)+up[i]*(cy-dot(centre,up)+dy/scale));
      const radius=h/(2*Math.tan(15*Math.PI/180)*scale);
      v.fieldOfView='30deg';v.cameraTarget=target.map(x=>x+'m').join(' ');v.cameraOrbit=`${theta}deg ${phi}deg ${radius}m`;
      requestAnimationFrame(()=>{ if(!this.viewer) return; v.jumpCameraToGoal();this.settingView=false;this.scheduleLines(); });
    }
    project(point) {
      const v=this.viewer,orbit=v.getCameraOrbit(),target=v.getCameraTarget(),w=this.stage.clientWidth,h=this.stage.clientHeight;
      const t=orbit.theta,p=orbit.phi,r=orbit.radius;
      const right=[Math.cos(t),0,-Math.sin(t)],up=[-Math.cos(p)*Math.sin(t),Math.sin(p),-Math.cos(p)*Math.cos(t)];
      const forward=[-Math.sin(p)*Math.sin(t),-Math.cos(p),-Math.sin(p)*Math.cos(t)];
      const q=[point[0]-target.x,point[1]-target.y,point[2]-target.z];
      const dot=a=>a.reduce((s,x,i)=>s+x*q[i],0),depth=r+dot(forward);
      if(depth<=0) return null;
      const scale=h/(2*r*Math.tan(v.getFieldOfView()*Math.PI/360));
      return {x:w/2+dot(right)*scale,y:h/2-dot(up)*scale};
    }
    rememberGuides(cloud) {
      // Store the current dimension plane in model coordinates, so its physical
      // span and labels remain attached while the camera rotates or zooms.
      const o=this.viewer.getCameraOrbit(),t=this.viewer.getCameraTarget();
      const right=[Math.cos(o.theta),0,-Math.sin(o.theta)];
      const up=[-Math.cos(o.phi)*Math.sin(o.theta),Math.sin(o.phi),-Math.cos(o.phi)*Math.cos(o.theta)];
      const outward=[Math.sin(o.phi)*Math.sin(o.theta),Math.cos(o.phi),Math.sin(o.phi)*Math.cos(o.theta)];
      const dot=(a,b)=>a.reduce((v,x,i)=>v+x*b[i],0),target=[t.x,t.y,t.z];
      const depth=Math.max(...cloud.map(p=>dot(p,outward)))-dot(target,outward);
      const scale=this.stage.clientHeight/(2*o.radius*Math.tan(this.viewer.getFieldOfView()*Math.PI/360));
      const world=(x,y)=>target.map((v,i)=>v+right[i]*(x-this.stage.clientWidth/2)/scale+up[i]*(this.stage.clientHeight/2-y)/scale+outward[i]*depth);
      this.orbitGuides=[...this.svg.children].filter(n=>n.tagName==='line'||n.tagName==='text').map(n=>n.tagName==='line'?{type:'line',className:n.getAttribute('class'),a:world(+n.getAttribute('x1'),+n.getAttribute('y1')),b:world(+n.getAttribute('x2'),+n.getAttribute('y2'))}:{type:'label',point:world(+n.getAttribute('x'),+n.getAttribute('y')),text:n.textContent});
    }
    drawOrbitGuides() {
      const ns='http://www.w3.org/2000/svg',w=this.stage.clientWidth,h=this.stage.clientHeight;
      this.svg.setAttribute('viewBox',`0 0 ${w} ${h}`);
      const node=(tag,attrs,text)=>{const n=document.createElementNS(ns,tag);for(const[k,v]of Object.entries(attrs))n.setAttribute(k,v);if(text)n.textContent=text;this.svg.append(n)};
      const labels=[];
      for(const guide of this.orbitGuides||[]) {
        if(guide.type==='line') {
          const a=this.project(guide.a),b=this.project(guide.b);if(!a||!b)continue;
          node('line',{x1:a.x,y1:a.y,x2:b.x,y2:b.y,class:guide.className});
        } else {
          const p=this.project(guide.point);if(!p)continue;
          const x=Math.max(36,Math.min(w-36,p.x));let y=Math.max(18,Math.min(h-15,p.y));
          for(const previous of labels)if(Math.abs(previous.x-x)<72&&Math.abs(previous.y-y)<27)y=previous.y+28<h-15?previous.y+28:previous.y-28;
          labels.push({x,y,text:guide.text});
        }
      }
      for(const {x,y,text}of labels){node('rect',{x:x-34,y:y-14,width:68,height:23,rx:4,class:'fit3d-label-bg'});node('text',{x,y,'text-anchor':'middle',class:'fit3d-label'},text)}
    }
    scheduleLines() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.drawLines(); });
    }
    drawLines() {
      if(!this.ready || !this.modal?.hasAttribute('open')) return;
      this.svg.replaceChildren();
      if(this.currentView==='free') { this.drawOrbitGuides(); return; }
      const w=this.stage.clientWidth,h=this.stage.clientHeight;
      if(!w || !h) return;
      this.svg.setAttribute('viewBox',`0 0 ${w} ${h}`);
      const lens=this.config.parts[this.partName]||Object.values(this.config.parts)[0];
      const cloud=this.mode==='lens'?lens.visibleOutline:this.config.frameOutlines[this.currentView];
      const projected=cloud.map(p=>this.project(p)).filter(Boolean);
      if(projected.length<2) return;
      const minX=Math.min(...projected.map(p=>p.x)),maxX=Math.max(...projected.map(p=>p.x));
      const minY=Math.min(...projected.map(p=>p.y)),maxY=Math.max(...projected.map(p=>p.y));
      const near=(axis,value)=>projected.filter(p=>Math.abs(p[axis]-value)<.6);
      const left=near('x',minX).sort((a,b)=>a.y-b.y)[0],right=near('x',maxX).sort((a,b)=>a.y-b.y)[0];
      const top=near('y',minY).sort((a,b)=>this.mode==='lens'?a.x-b.x:b.x-a.x)[0],bottom=near('y',maxY).sort((a,b)=>this.mode==='lens'?a.x-b.x:b.x-a.x)[0];
      const ns='http://www.w3.org/2000/svg';
      const node=(tag,attrs,text)=>{const n=document.createElementNS(ns,tag);for(const[k,v]of Object.entries(attrs))n.setAttribute(k,v);if(text)n.textContent=text;this.svg.append(n);return n};
      const line=(x1,y1,x2,y2,cls)=>node('line',{x1,y1,x2,y2,class:cls});
      const label=(x,y,number)=>{
        x=Math.max(35,Math.min(w-35,x));y=Math.max(18,Math.min(h-15,y));
        node('rect',{x:x-34,y:y-14,width:68,height:23,rx:4,class:'fit3d-label-bg'});
        node('text',{x,y,'text-anchor':'middle',class:'fit3d-label'},`${number.toFixed(1)} mm`);
      };
      const horizontal=(a,b,y,number)=>{
        line(a.x,a.y-3,a.x,y-6,'fit3d-extension');line(b.x,b.y-3,b.x,y-6,'fit3d-extension');
        line(a.x,y,b.x,y,'fit3d-dimension');
        line(a.x-3,y+4,a.x+3,y-4,'fit3d-tick');line(b.x-3,y+4,b.x+3,y-4,'fit3d-tick');
        label((a.x+b.x)/2,y-5,number);
      };
      const vertical=(a,b,x,number,leftSide)=>{
        line(a.x+(leftSide?-3:3),a.y,x+(leftSide?-6:6),a.y,'fit3d-extension');line(b.x+(leftSide?-3:3),b.y,x+(leftSide?-6:6),b.y,'fit3d-extension');
        line(x,a.y,x,b.y,'fit3d-dimension');
        line(x-4,a.y+3,x+4,a.y-3,'fit3d-tick');line(x-4,b.y+3,x+4,b.y-3,'fit3d-tick');
        label(x+(leftSide?-38:38),(a.y+b.y)/2+4,number);
      };
      if(this.mode==='lens') {
        horizontal(left,right,Math.max(30,minY-23),lens.width);
        vertical(top,bottom,Math.max(45,minX-20),lens.height,true);
      } else if(this.currentView==='front') horizontal(left,right,Math.max(30,minY-24),this.config.dimensions.width);
      else if(this.currentView==='side') horizontal(left,right,Math.max(30,minY-24),this.config.dimensions.length);
      else { horizontal(left,right,Math.max(30,minY-24),this.config.dimensions.width);vertical(top,bottom,Math.min(w-48,maxX+20),this.config.dimensions.length,false); }
      this.rememberGuides(cloud);
    }

  }
  customElements.define('two-nu-fit3d', Fit3D);
})();
